import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Wand2, Loader2, Upload, X, Download, Sun, Contrast, 
  Paintbrush, Sparkles, Image, Check, RefreshCw, History, Clock,
  Shirt, Search, ArrowRight, ZoomIn
} from 'lucide-react';
import { toast } from 'sonner';

// Image Modal Component for enlarging images
const ImageModal = ({ isOpen, onClose, imageSrc, title }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-[90vh] w-full">
        <Button
          variant="ghost"
          size="sm"
          className="absolute -top-10 right-0 text-white hover:bg-white/20"
          onClick={onClose}
        >
          <X className="w-5 h-5 mr-1" />
          Cerrar
        </Button>
        {title && (
          <p className="text-white text-center mb-2 font-medium">{title}</p>
        )}
        <img 
          src={imageSrc}
          alt={title || "Enlarged"}
          className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': token ? `Bearer ${token}` : '',
    }
  });
};

const ENHANCEMENT_OPTIONS = [
  { id: 'lighting', name: 'Iluminación', nameEn: 'Lighting', icon: Sun, desc: 'Mejora brillo y exposición' },
  { id: 'sharpness', name: 'Nitidez', nameEn: 'Sharpness', icon: Contrast, desc: 'Aumenta claridad y detalle' },
  { id: 'background', name: 'Fondo', nameEn: 'Background', icon: Paintbrush, desc: 'Limpia o reemplaza el fondo' },
  { id: 'color_correction', name: 'Corrección de Color', nameEn: 'Color Correction', icon: Sparkles, desc: 'Mejora saturación y balance' },
  { id: 'remove_noise', name: 'Reducir Ruido', nameEn: 'Remove Noise', icon: Wand2, desc: 'Elimina imperfecciones' },
];

const BACKGROUND_TYPES = [
  { id: 'white', name: 'Blanco', nameEn: 'White', desc: 'Fondo blanco profesional' },
  { id: 'gradient', name: 'Degradado', nameEn: 'Gradient', desc: 'Gradiente gris-blanco sutil' },
  { id: 'studio', name: 'Estudio', nameEn: 'Studio', desc: 'Setup de estudio profesional' },
];

const PhotoOptimizer = ({ initialImage, onImageProcessed, onSendToDesigner, onSendToDesignerAsFabric, onSendToVisualSearch, onCreditError }) => {
  const { isUSA } = useLocale();
  
  // State
  const [activeTab, setActiveTab] = useState('optimize'); // 'optimize' or 'history'
  const [originalImage, setOriginalImage] = useState(null);
  const [originalPreview, setOriginalPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedEnhancements, setSelectedEnhancements] = useState(['lighting', 'sharpness', 'background']);
  const [backgroundType, setBackgroundType] = useState('white');
  const [showAI, setShowAI] = useState(true);
  const [customPrompt, setCustomPrompt] = useState(''); // Custom enhancement prompt
  
  // History state
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Modal state for image enlargement
  const [modalImage, setModalImage] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  
  const fileInputRef = useRef(null);
  
  // Open enlarged image modal
  const openImageModal = (imageSrc, title = '') => {
    setModalImage(imageSrc);
    setModalTitle(title);
  };
  
  const closeImageModal = () => {
    setModalImage(null);
    setModalTitle('');
  };

  // Fetch history
  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/texvision/photo-optimizer/history?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch (e) {
      console.error('Error fetching history:', e);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Load history when switching to history tab
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  // Load initial image from other tools
  useEffect(() => {
    if (initialImage) {
      setOriginalImage(initialImage);
      setOriginalPreview(`data:image/png;base64,${initialImage}`);
      setResult(null);
      setActiveTab('optimize');
      toast.success(isUSA ? 'Image loaded! Click Optimize to enhance.' : '¡Imagen cargada! Haz clic en Optimizar para mejorar.');
      if (onImageProcessed) {
        onImageProcessed();
      }
    }
  }, [initialImage, onImageProcessed, isUSA]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(isUSA ? 'Image must be less than 10MB' : 'La imagen debe ser menor a 10MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        setOriginalImage(base64);
        setOriginalPreview(e.target.result);
        setResult(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleEnhancement = (id) => {
    setSelectedEnhancements(prev => 
      prev.includes(id) 
        ? prev.filter(e => e !== id)
        : [...prev, id]
    );
  };

  const handleOptimize = async () => {
    if (!originalImage) {
      toast.error(isUSA ? 'Please upload an image' : 'Por favor sube una imagen');
      return;
    }
    
    if (selectedEnhancements.length === 0 && !customPrompt.trim()) {
      toast.error(isUSA ? 'Select at least one enhancement or add a custom instruction' : 'Selecciona al menos una mejora o añade una instrucción personalizada');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const res = await fetchWithAuth(`${API_URL}/api/texvision/photo-optimizer/enhance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_base64: originalImage,
          enhancements: selectedEnhancements,
          background_type: backgroundType,
          custom_prompt: customPrompt.trim() || null
        })
      });

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Error al optimizar');
      }

      setResult(data);
      fetchHistory(); // Refresh history
      toast.success(isUSA ? 'Photo optimized!' : '¡Foto optimizada!');
    } catch (e) {
      console.error('Photo optimizer error:', e);
      if (onCreditError && onCreditError(e.message)) {
        // Error was handled by parent
      } else {
        toast.error(e.message || (isUSA ? 'Optimization failed' : 'Error en la optimización'));
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = (base64, filename = 'optimized.jpg') => {
    const link = document.createElement('a');
    link.href = `data:image/jpeg;base64,${base64}`;
    link.download = filename;
    link.click();
    toast.success(isUSA ? 'Image downloaded' : 'Imagen descargada');
  };

  const resetOptimizer = () => {
    setOriginalImage(null);
    setOriginalPreview(null);
    setResult(null);
  };

  // Get the current enhanced image
  const getEnhancedPreview = () => {
    if (!result) return null;
    if (showAI && result.ai_enhanced_base64) {
      return `data:image/jpeg;base64,${result.ai_enhanced_base64}`;
    }
    return `data:image/jpeg;base64,${result.enhanced_base64}`;
  };

  // Get the current enhanced base64 (without data prefix)
  const getEnhancedBase64 = () => {
    if (!result) return null;
    if (showAI && result.ai_enhanced_base64) {
      return result.ai_enhanced_base64;
    }
    return result.enhanced_base64;
  };

  // Send to other tools
  const sendToDesignerAsPattern = () => {
    const enhancedBase64 = getEnhancedBase64();
    if (enhancedBase64 && onSendToDesigner) {
      onSendToDesigner(enhancedBase64);
      toast.success(isUSA ? 'Image sent as pattern reference' : 'Imagen enviada como referencia de patrón');
    }
  };

  const sendToDesignerAsFabric = () => {
    const enhancedBase64 = getEnhancedBase64();
    if (enhancedBase64 && onSendToDesignerAsFabric) {
      onSendToDesignerAsFabric(enhancedBase64);
      toast.success(isUSA ? 'Image sent as fabric texture' : 'Imagen enviada como textura de tela');
    }
  };

  const sendToVisualSearch = () => {
    const enhancedBase64 = getEnhancedBase64();
    if (enhancedBase64 && onSendToVisualSearch) {
      onSendToVisualSearch(enhancedBase64);
      toast.success(isUSA ? 'Image sent to Visual Search' : 'Imagen enviada al Buscador Visual');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="photo-optimizer">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Wand2 className="w-6 h-6 text-amber-600" />
            {isUSA ? 'Photo Optimizer' : 'Optimizador de Fotos'}
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            {isUSA 
              ? 'Enhance your product photos with AI for professional catalog quality' 
              : 'Mejora tus fotos de productos con IA para calidad profesional de catálogo'}
          </p>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'optimize' ? 'default' : 'outline'}
            onClick={() => setActiveTab('optimize')}
            className={activeTab === 'optimize' ? 'bg-amber-600 hover:bg-amber-700' : ''}
          >
            <Wand2 className="w-4 h-4 mr-2" />
            {isUSA ? 'Optimize' : 'Optimizar'}
          </Button>
          <Button
            variant={activeTab === 'history' ? 'default' : 'outline'}
            onClick={() => setActiveTab('history')}
            className={activeTab === 'history' ? 'bg-amber-600 hover:bg-amber-700' : ''}
          >
            <History className="w-4 h-4 mr-2" />
            {isUSA ? 'History' : 'Historial'}
            {history.length > 0 && (
              <Badge className="ml-2 bg-white/20 text-white">{history.length}</Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Optimize Tab */}
      {activeTab === 'optimize' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Upload & Options */}
          <div className="space-y-6">
            {/* Upload Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  {isUSA ? 'Upload Photo' : 'Subir Foto'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  className="hidden"
                />
                
                {originalPreview ? (
                  <div className="space-y-3">
                    <div className="relative">
                      <img 
                        src={originalPreview} 
                        alt="Original" 
                        className="w-full h-48 object-contain rounded-lg border-2 border-slate-200 bg-slate-50"
                      />
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        className="absolute top-2 right-2 w-7 h-7 p-0"
                        onClick={resetOptimizer}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-center text-slate-500">
                      {isUSA ? 'Original image' : 'Imagen original'}
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-48 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-amber-400 hover:bg-amber-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                      <Upload className="w-6 h-6 text-amber-600" />
                    </div>
                    <p className="font-medium text-slate-700 text-sm">
                      {isUSA ? 'Click to upload' : 'Clic para subir'}
                    </p>
                  </button>
                )}
              </CardContent>
            </Card>

            {/* Enhancement Options */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  {isUSA ? 'Enhancements' : 'Mejoras'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {ENHANCEMENT_OPTIONS.map((opt) => {
                  const IconComponent = opt.icon;
                  const isSelected = selectedEnhancements.includes(opt.id);
                  return (
                    <div 
                      key={opt.id}
                      onClick={() => toggleEnhancement(opt.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        isSelected 
                          ? 'border-amber-400 bg-amber-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isSelected ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {isSelected ? <Check className="w-4 h-4" /> : <IconComponent className="w-4 h-4" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{isUSA ? opt.nameEn : opt.name}</p>
                        <p className="text-xs text-slate-500">{opt.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Background Type */}
            {selectedEnhancements.includes('background') && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Paintbrush className="w-5 h-5" />
                    {isUSA ? 'Background Type' : 'Tipo de Fondo'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2">
                    {BACKGROUND_TYPES.map((bg) => (
                      <button
                        key={bg.id}
                        onClick={() => setBackgroundType(bg.id)}
                        className={`p-3 rounded-lg border text-center transition-colors ${
                          backgroundType === bg.id 
                            ? 'border-amber-500 bg-amber-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <p className="font-medium text-sm">{isUSA ? bg.nameEn : bg.name}</p>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Custom Enhancement Prompt */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  {isUSA ? 'Custom Enhancement (Optional)' : 'Personalización (Opcional)'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder={isUSA 
                    ? 'E.g., "Crop only the garment", "Remove background distractions", "Focus on the fabric texture"...'
                    : 'Ej: "Recortar solo la prenda", "Eliminar distracciones del fondo", "Enfocar solo la tela"...'}
                  className="w-full px-3 py-2 text-sm border rounded-md resize-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  rows={2}
                />
                <p className="text-xs text-slate-400 mt-1">
                  {isUSA 
                    ? 'Describe any specific changes you need that are not in the options above'
                    : 'Describe cambios específicos que no están en las opciones anteriores'}
                </p>
              </CardContent>
            </Card>

            {/* Optimize Button */}
            <Button 
              className="w-full bg-amber-600 hover:bg-amber-700 py-6 text-lg"
              onClick={handleOptimize}
              disabled={loading || !originalImage}
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {isUSA ? 'Optimizing...' : 'Optimizando...'}</>
              ) : (
                <><Wand2 className="w-5 h-5 mr-2" /> {isUSA ? 'Optimize Photo' : 'Optimizar Foto'}</>
              )}
            </Button>
          </div>

          {/* Right: Results (2 columns) */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Image className="w-5 h-5" />
                    {isUSA ? 'Result' : 'Resultado'}
                  </CardTitle>
                  {result && result.ai_enhanced_base64 && (
                    <div className="flex gap-2">
                      <Button
                        variant={showAI ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setShowAI(true)}
                        className={showAI ? 'bg-amber-600' : ''}
                      >
                        <Sparkles className="w-3 h-3 mr-1" />
                        AI
                      </Button>
                      <Button
                        variant={!showAI ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setShowAI(false)}
                        className={!showAI ? 'bg-amber-600' : ''}
                      >
                        {isUSA ? 'Basic' : 'Básico'}
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!result ? (
                  <div className="flex flex-col items-center justify-center h-80 text-slate-400">
                    <Wand2 className="w-16 h-16 mb-4 opacity-30" />
                    <p className="text-center">
                      {isUSA 
                        ? 'Upload a photo and click "Optimize" to see results' 
                        : 'Sube una foto y haz clic en "Optimizar" para ver resultados'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Side by side comparison */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Original */}
                      <div>
                        <p className="text-sm font-medium text-slate-600 mb-2 text-center">
                          {isUSA ? 'Original' : 'Original'}
                        </p>
                        <div 
                          className="aspect-square bg-slate-100 rounded-lg overflow-hidden border cursor-pointer hover:ring-2 hover:ring-slate-400 transition-all relative group"
                          onClick={() => openImageModal(originalPreview, isUSA ? 'Original' : 'Original')}
                        >
                          <img 
                            src={originalPreview}
                            alt="Original"
                            className="w-full h-full object-contain"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      </div>
                      
                      {/* Enhanced */}
                      <div>
                        <p className="text-sm font-medium text-amber-600 mb-2 text-center flex items-center justify-center gap-1">
                          <Sparkles className="w-4 h-4" />
                          {isUSA ? 'Enhanced' : 'Mejorada'}
                          {showAI && result.ai_enhanced_base64 && (
                            <Badge className="ml-1 bg-amber-100 text-amber-700 text-xs">AI</Badge>
                          )}
                        </p>
                        <div 
                          className="aspect-square bg-slate-100 rounded-lg overflow-hidden border-2 border-amber-300 cursor-pointer hover:ring-2 hover:ring-amber-400 transition-all relative group"
                          onClick={() => openImageModal(getEnhancedPreview(), isUSA ? 'Enhanced' : 'Mejorada')}
                        >
                          <img 
                            src={getEnhancedPreview()}
                            alt="Enhanced"
                            className="w-full h-full object-contain"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Enhancement badges */}
                    <div className="flex flex-wrap gap-2 justify-center">
                      {result.enhancements_applied?.map((e, i) => {
                        const opt = ENHANCEMENT_OPTIONS.find(o => o.id === e);
                        return (
                          <Badge key={i} variant="outline" className="text-xs">
                            <Check className="w-3 h-3 mr-1 text-emerald-500" />
                            {opt ? (isUSA ? opt.nameEn : opt.name) : e}
                          </Badge>
                        );
                      })}
                    </div>

                    {/* Download buttons */}
                    <div className="flex gap-3 justify-center pt-2">
                      <Button 
                        variant="outline"
                        onClick={() => downloadImage(result.enhanced_base64, 'foto_mejorada_basica.jpg')}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {isUSA ? 'Download Basic' : 'Descargar Básica'}
                      </Button>
                      {result.ai_enhanced_base64 && (
                        <Button 
                          className="bg-amber-600 hover:bg-amber-700"
                          onClick={() => downloadImage(result.ai_enhanced_base64, 'foto_mejorada_ai.jpg')}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          <Sparkles className="w-4 h-4 mr-1" />
                          {isUSA ? 'Download AI' : 'Descargar AI'}
                        </Button>
                      )}
                    </div>

                    {/* Send to other tools */}
                    <div className="border-t pt-4 mt-4">
                      <p className="text-sm text-slate-500 mb-3 text-center">
                        {isUSA ? 'Send enhanced image to Fashion Designer:' : 'Enviar al Diseñador de Modas:'}
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center mb-3">
                        {onSendToDesigner && (
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={sendToDesignerAsPattern}
                            className="text-sm"
                          >
                            <Shirt className="w-4 h-4 mr-1" />
                            {isUSA ? 'As Pattern (Garment)' : 'Como Patrón (Prenda)'}
                            <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                        {onSendToDesignerAsFabric && (
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={sendToDesignerAsFabric}
                            className="text-sm border-purple-300 hover:bg-purple-50"
                          >
                            <Paintbrush className="w-4 h-4 mr-1 text-purple-600" />
                            {isUSA ? 'As Fabric Texture' : 'Como Textura de Tela'}
                            <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                      </div>
                      {onSendToVisualSearch && (
                        <div className="flex justify-center">
                          <Button 
                            variant="ghost"
                            size="sm"
                            onClick={sendToVisualSearch}
                            className="text-slate-600"
                          >
                            <Search className="w-4 h-4 mr-1" />
                            {isUSA ? 'Send to Visual Search' : 'Enviar al Buscador Visual'}
                            <ArrowRight className="w-3 h-3 ml-1" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-amber-600" />
              {isUSA ? 'Enhancement History' : 'Historial de Mejoras'}
            </CardTitle>
            <CardDescription>
              {isUSA 
                ? 'Your previous photo enhancements' 
                : 'Tus mejoras de fotos anteriores'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : history.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <History className="w-16 h-16 mb-4 opacity-30" />
                <p className="text-center">
                  {isUSA ? 'No enhancement history yet' : 'Aún no hay historial de mejoras'}
                </p>
                <Button 
                  className="mt-4 bg-amber-600 hover:bg-amber-700"
                  onClick={() => setActiveTab('optimize')}
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  {isUSA ? 'Start optimizing' : 'Comenzar a optimizar'}
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {history.map((item, idx) => (
                  <div 
                    key={item.enhancement_id || idx}
                    className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* Before/After thumbnails with click to enlarge */}
                    <div className="flex">
                      <div 
                        className="w-1/2 aspect-square bg-slate-100 relative cursor-pointer group"
                        onClick={() => item.original_thumbnail && openImageModal(
                          `data:image/jpeg;base64,${item.original_thumbnail}`,
                          isUSA ? 'Original' : 'Original'
                        )}
                      >
                        {item.original_thumbnail ? (
                          <>
                            <img 
                              src={`data:image/jpeg;base64,${item.original_thumbnail}`}
                              alt="Original"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                              <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-8 h-8 text-slate-300" />
                          </div>
                        )}
                        <span className="absolute bottom-1 left-1 text-xs bg-black/50 text-white px-1 rounded">
                          {isUSA ? 'Before' : 'Antes'}
                        </span>
                      </div>
                      <div 
                        className="w-1/2 aspect-square bg-slate-100 relative border-l-2 border-amber-400 cursor-pointer group"
                        onClick={() => item.enhanced_thumbnail && openImageModal(
                          `data:image/jpeg;base64,${item.enhanced_thumbnail}`,
                          isUSA ? 'Enhanced' : 'Mejorada'
                        )}
                      >
                        {item.enhanced_thumbnail ? (
                          <>
                            <img 
                              src={`data:image/jpeg;base64,${item.enhanced_thumbnail}`}
                              alt="Enhanced"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                              <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Sparkles className="w-8 h-8 text-amber-300" />
                          </div>
                        )}
                        <span className="absolute bottom-1 right-1 text-xs bg-amber-500 text-white px-1 rounded">
                          {isUSA ? 'After' : 'Después'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Info */}
                    <div className="p-3">
                      <div className="flex flex-wrap gap-1 mb-2">
                        {item.enhancements_applied?.slice(0, 3).map((e, i) => {
                          const opt = ENHANCEMENT_OPTIONS.find(o => o.id === e);
                          return (
                            <Badge key={i} variant="outline" className="text-xs py-0">
                              {opt ? (isUSA ? opt.nameEn : opt.name) : e}
                            </Badge>
                          );
                        })}
                        {item.has_ai_enhancement && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs py-0">AI</Badge>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(item.created_at)}
                        </span>
                        {item.background_type && (
                          <span className="capitalize">{item.background_type}</span>
                        )}
                      </div>
                      
                      {/* Send to other tools buttons */}
                      {item.enhanced_thumbnail && (onSendToDesigner || onSendToVisualSearch) && (
                        <div className="flex gap-1 pt-2 border-t">
                          {onSendToDesigner && (
                            <Button 
                              variant="ghost"
                              size="sm"
                              className="flex-1 h-7 text-xs"
                              onClick={() => {
                                onSendToDesigner(item.enhanced_thumbnail);
                                toast.info(isUSA ? 'Thumbnail sent (low quality)' : 'Miniatura enviada (baja calidad)');
                              }}
                              title={isUSA ? 'Note: This sends a low-resolution thumbnail' : 'Nota: Envía una miniatura de baja resolución'}
                            >
                              <Shirt className="w-3 h-3 mr-1" />
                              {isUSA ? 'Designer' : 'Diseñador'}
                            </Button>
                          )}
                          {onSendToVisualSearch && (
                            <Button 
                              variant="ghost"
                              size="sm"
                              className="flex-1 h-7 text-xs"
                              onClick={() => {
                                onSendToVisualSearch(item.enhanced_thumbnail);
                                toast.info(isUSA ? 'Thumbnail sent (low quality)' : 'Miniatura enviada (baja calidad)');
                              }}
                              title={isUSA ? 'Note: This sends a low-resolution thumbnail' : 'Nota: Envía una miniatura de baja resolución'}
                            >
                              <Search className="w-3 h-3 mr-1" />
                              {isUSA ? 'Search' : 'Buscador'}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Image Enlargement Modal */}
      <ImageModal 
        isOpen={!!modalImage}
        onClose={closeImageModal}
        imageSrc={modalImage}
        title={modalTitle}
      />
    </div>
  );
};

export default PhotoOptimizer;
